import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { AlertTriangle, ArrowLeftRight, History, KeyRound, LayoutDashboard, LogOut, Menu, Package, Settings, Store, Table2, TrendingUp, Upload, Users, Wallet, X } from "lucide-react";

function CambiaPasswordDialog({ open, onClose }) {
  const [f, setF] = useState({ vecchia: "", nuova: "", conferma: "" });
  const [busy, setBusy] = useState(false);
  const inp = "border border-slate-300 rounded-md px-2 py-1.5 text-sm w-full";
  const submit = async () => {
    if (f.nuova.length < 8) { toast.error("La nuova password deve avere almeno 8 caratteri"); return; }
    if (f.nuova !== f.conferma) { toast.error("La nuova password e la conferma non coincidono"); return; }
    setBusy(true);
    try {
      await api.post("/auth/cambia-password", { vecchia_password: f.vecchia, nuova_password: f.nuova });
      toast.success("Password aggiornata");
      setF({ vecchia: "", nuova: "", conferma: "" });
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="cambia-password-dialog">
        <DialogHeader><DialogTitle>Cambia la tua password</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <label className="text-xs text-slate-500 block">Password attuale
            <input data-testid="cambia-password-vecchia" type="password" className={inp} value={f.vecchia}
              onChange={(e) => setF({ ...f, vecchia: e.target.value })} /></label>
          <label className="text-xs text-slate-500 block">Nuova password (minimo 8 caratteri)
            <input data-testid="cambia-password-nuova" type="password" className={inp} value={f.nuova}
              onChange={(e) => setF({ ...f, nuova: e.target.value })} /></label>
          <label className="text-xs text-slate-500 block">Conferma nuova password
            <input data-testid="cambia-password-conferma" type="password" className={inp} value={f.conferma}
              onChange={(e) => setF({ ...f, conferma: e.target.value })} /></label>
          <button data-testid="cambia-password-save" onClick={submit} disabled={busy || !f.vecchia || !f.nuova}
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-2 w-full disabled:opacity-40 transition-colors">
            {busy ? "Salvataggio…" : "Aggiorna password"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  { path: "/", label: "Dashboard", testid: "tab-dashboard", icon: LayoutDashboard },
  { path: "/cassa", label: "Cassa COD", testid: "tab-cassa-cod", icon: Wallet },
  { path: "/ordini", label: "Ordini", testid: "tab-orders-grid", icon: Table2 },
  { path: "/adv", label: "Spesa ADV", testid: "tab-adv", icon: TrendingUp, master: true },
  { path: "/quote-soci", label: "Quote Soci", testid: "tab-partner-shares", icon: Users },
  { path: "/prodotti", label: "Prodotti", testid: "tab-prodotti", icon: Package, master: true },
  { path: "/dati-mancanti", label: "Dati mancanti", testid: "tab-missing-data", icon: AlertTriangle, master: true },
  { path: "/divergenze", label: "Divergenze", testid: "tab-divergenze", icon: ArrowLeftRight, master: true },
  { path: "/import", label: "Import CSV", testid: "tab-import-csv", icon: Upload, master: true },
  { path: "/configurazione", label: "Configurazione", testid: "tab-store-config", icon: Settings, master: true },
  { path: "/log", label: "Log modifiche", testid: "tab-audit-logs", icon: History, master: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMaster = user.role === "master";
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(isMaster ? "all" : user.store_id);
  const [mode, setMode] = useState("mese");
  const now = new Date();
  const [mese, setMese] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [drawer, setDrawer] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)).catch(() => {}); }, []);
  useEffect(() => { setDrawer(false); }, [location.pathname]);

  const { effFrom, effTo } = useMemo(() => {
    if (mode === "range" && from && to) return { effFrom: from, effTo: to };
    const [y, m] = mese.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return { effFrom: `${mese}-01`, effTo: `${mese}-${String(last).padStart(2, "0")}` };
  }, [mode, mese, from, to]);

  const ctx = { stores, storeId, setStoreId, from: effFrom, to: effTo, isMaster };
  const tabs = TABS.filter((t) => !t.master || isMaster);

  const SidebarContent = ({ suffix, onNavigate }) => (
    <>
      <div className="px-5 pt-5 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
            <Store className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-white text-sm leading-tight">My Brand Online SL</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Shopify Group</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {tabs.map((t) => {
          const active = location.pathname === t.path;
          const Icon = t.icon;
          return (
            <Link key={t.path} to={t.path} onClick={onNavigate}
              data-testid={suffix ? `${t.testid}-${suffix}` : t.testid}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150 ${
                active ? "bg-indigo-600 text-white font-semibold shadow-sm" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <Icon className="w-4 h-4 shrink-0" />
              {t.label}
            </Link>
          );
        })}
      </nav>
      <div className="px-4 py-4 border-t border-white/10">
        <div className="text-xs text-slate-400 truncate">{user.name || user.email}</div>
        <div className="flex items-center justify-between mt-1.5">
          <span data-testid={suffix ? `role-badge-${suffix}` : "role-badge"}
            className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${isMaster ? "bg-indigo-600/25 text-indigo-300" : "bg-emerald-500/15 text-emerald-300"}`}>
            {isMaster ? "Master" : "Socio"}
          </span>
          <div className="flex items-center">
            <button data-testid={suffix ? `cambia-password-${suffix}` : "cambia-password-btn"} title="Cambia password"
              onClick={() => setShowPw(true)}
              className="text-slate-500 hover:text-white transition-colors duration-150 p-1.5">
              <KeyRound className="w-4 h-4" />
            </button>
            <button data-testid={suffix ? `logout-${suffix}` : "logout-button"} title="Esci"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="text-slate-500 hover:text-white transition-colors duration-150 p-1.5">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col bg-[#0B1220] z-40">
        <SidebarContent />
      </aside>

      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setDrawer(false)} />
          <aside data-testid="mobile-drawer" className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[#0B1220] flex flex-col fade-in">
            <button data-testid="drawer-close" onClick={() => setDrawer(false)}
              className="absolute top-4 right-3 text-slate-400 hover:text-white p-2"><X className="w-5 h-5" /></button>
            <SidebarContent suffix="drawer" onNavigate={() => setDrawer(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-60">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/70 px-4 sm:px-6 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="menu-open-button" onClick={() => setDrawer(true)}
              className="lg:hidden p-2 -ml-1 text-slate-600 hover:text-slate-900 min-h-[44px] min-w-[44px] flex items-center justify-center">
              <Menu className="w-5 h-5" />
            </button>
            {isMaster ? (
              <select data-testid="store-filter-select" value={storeId} onChange={(e) => setStoreId(e.target.value)}
                className="text-sm border border-slate-300 rounded-md px-2.5 py-2 bg-white max-w-[46vw]">
                <option value="all">Tutti gli store (consolidato)</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            ) : (
              <span data-testid="store-filter-select" className="text-sm font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-3 py-1.5">
                {stores.find((s) => s.id === user.store_id)?.nome || "Il tuo store"}
              </span>
            )}
            <div className="flex items-center gap-2">
              <select data-testid="period-filter-select" value={mode} onChange={(e) => setMode(e.target.value)}
                className="text-sm border border-slate-300 rounded-md px-2.5 py-2 bg-white">
                <option value="mese">Mese</option>
                <option value="range">Range date</option>
              </select>
              {mode === "mese" ? (
                <input data-testid="month-picker" type="month" value={mese} onChange={(e) => setMese(e.target.value)}
                  className="text-sm border border-slate-300 rounded-md px-2.5 py-2 bg-white" />
              ) : (
                <div data-testid="date-range-picker" className="flex items-center gap-1.5">
                  <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                    className="text-sm border border-slate-300 rounded-md px-2 py-2 bg-white w-[130px]" />
                  <span className="text-slate-400 text-sm">→</span>
                  <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                    className="text-sm border border-slate-300 rounded-md px-2 py-2 bg-white w-[130px]" />
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="px-4 sm:px-6 py-5 pb-24 lg:pb-8 max-w-[1500px] mx-auto">
          <Outlet context={ctx} />
        </main>
      </div>

      <nav data-testid="bottom-nav" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 flex">
        {tabs.slice(0, 4).map((t) => {
          const active = location.pathname === t.path;
          const Icon = t.icon;
          return (
            <Link key={t.path} to={t.path} data-testid={`${t.testid}-bottom`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[11px] transition-colors duration-150 ${active ? "text-indigo-600 font-semibold" : "text-slate-500"}`}>
              <Icon className="w-5 h-5" />
              {t.label}
            </Link>
          );
        })}
        <button data-testid="bottom-nav-menu" onClick={() => setDrawer(true)}
          className="flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[11px] text-slate-500">
          <Menu className="w-5 h-5" />
          Menu
        </button>
      </nav>
      <CambiaPasswordDialog open={showPw} onClose={() => setShowPw(false)} />
    </div>
  );
}
